import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { 
  Heart, 
  ShoppingCart, 
  Trash2, 
  Loader2, 
  BookOpen,
  Package,
  ExternalLink,
  ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Wishlist = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();

  useEffect(() => {
    if (isAuthenticated) {
      fetchWishlist();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchWishlist = async () => {
    try {
      const response = await axios.get(`${API}/wishlist`);
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
      toast.error('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (item) => {
    setRemovingId(item.id);
    try {
      await axios.post(`${API}/wishlist/remove`, {
        item_type: item.item_type,
        item_id: item.id
      });
      setItems(prev => prev.filter(i => i.id !== item.id));
      toast.success('Removed from wishlist');
    } catch (error) {
      toast.error('Failed to remove item');
    } finally {
      setRemovingId(null);
    }
  };

  const handleAddToCart = async (item) => {
    if (item.item_type === 'ebook') {
      // For ebooks, use the cart context with ebook data
      const success = await addToCart({
        product_id: item.id,
        ebook_id: item.id,
        name: item.name,
        price: item.price,
        image_url: item.cover_url,
        is_digital: true,
        quantity: 1,
      });
      if (success) {
        toast.success(`"${item.name}" added to cart`);
      }
    } else {
      // For regular products
      const success = await addToCart(item.id);
      if (success) {
        toast.success(`"${item.name}" added to cart`);
      }
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <Heart className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
        <h2 className="font-serif text-2xl font-bold mb-2">Sign in to view your Wishlist</h2>
        <p className="text-muted-foreground mb-6">
          Save your favorite items and access them anytime
        </p>
        <Link to="/login">
          <Button className="btn-accent">Sign In</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="wishlist-page">
      <div className="mb-8">
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-2">
          My Wishlist
        </h1>
        <p className="text-muted-foreground">
          {items.length} {items.length === 1 ? 'item' : 'items'} saved
        </p>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16" data-testid="empty-wishlist">
          <Heart className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
          <h2 className="font-serif text-xl font-bold mb-2">Your wishlist is empty</h2>
          <p className="text-muted-foreground mb-6">
            Browse our collection and save items you love
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/products">
              <Button variant="outline" className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                Browse Products
              </Button>
            </Link>
            <Link to="/ebooks">
              <Button variant="outline" className="flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Browse E-books
              </Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid gap-4" data-testid="wishlist-items">
          {items.map((item) => (
            <Card key={item.id} className="overflow-hidden" data-testid={`wishlist-item-${item.id}`}>
              <CardContent className="p-0">
                <div className="flex flex-col sm:flex-row">
                  {/* Image */}
                  <Link 
                    to={item.item_type === 'ebook' ? `/ebooks/${item.id}` : `/product/${item.id}`}
                    className="sm:w-40 h-40 sm:h-auto flex-shrink-0"
                  >
                    <img
                      src={item.item_type === 'ebook' ? item.cover_url : item.image_url || 'https://images.pexels.com/photos/256450/pexels-photo-256450.jpeg'}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </Link>

                  {/* Details */}
                  <div className="flex-1 p-4 flex flex-col">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div>
                        <Badge variant="outline" className="mb-2">
                          {item.item_type === 'ebook' ? (
                            <><BookOpen className="h-3 w-3 mr-1" /> E-book</>
                          ) : (
                            <><Package className="h-3 w-3 mr-1" /> Product</>
                          )}
                        </Badge>
                        <Link 
                          to={item.item_type === 'ebook' ? `/ebooks/${item.id}` : `/product/${item.id}`}
                          className="block"
                        >
                          <h3 className="font-serif text-lg font-semibold text-foreground hover:text-primary transition-colors">
                            {item.name}
                          </h3>
                        </Link>
                        {item.category_name && (
                          <p className="text-sm text-muted-foreground">{item.category_name}</p>
                        )}
                      </div>
                      <p className="price-tag text-xl font-bold text-primary whitespace-nowrap">
                        ${item.price?.toFixed(2)}
                      </p>
                    </div>

                    {item.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-4 flex-1">
                        {item.description}
                      </p>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-2 mt-auto">
                      {item.affiliate_url ? (
                        <a href={item.affiliate_url} target="_blank" rel="noopener noreferrer">
                          <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Buy from Partner
                          </Button>
                        </a>
                      ) : (
                        <Button 
                          className="btn-accent"
                          onClick={() => handleAddToCart(item)}
                          data-testid={`add-to-cart-${item.id}`}
                        >
                          <ShoppingCart className="h-4 w-4 mr-2" />
                          Add to Cart
                        </Button>
                      )}
                      
                      <Link to={item.item_type === 'ebook' ? `/ebooks/${item.id}` : `/product/${item.id}`}>
                        <Button variant="outline">
                          View Details
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                      </Link>

                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 ml-auto"
                        onClick={() => handleRemove(item)}
                        disabled={removingId === item.id}
                        data-testid={`remove-wishlist-${item.id}`}
                      >
                        {removingId === item.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Wishlist;
